import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Portal } from "@chakra-ui/portal";
import Navbar from "components/navbar";
import routes from "routes";
import { useTranslation } from "react-i18next";
import AgentChat from "layouts/agent/agent-chat";
import WorkspacesView from "views/workspaces/WorkspacesView";
import RoleInfoLayout from "layouts/role-info";
import CurrentOrgInfoLayout from "layouts/current-organisations";
import CurrentWorkspaceInfoLayout from "layouts/current-workspace";
import { UserProfile } from "views/profile/UserProfile";
import Roles from "views/roles";
import LibraryViewSwitch from "views/library/LibraryViewSwitch";
import { LibraryProvider } from "views/library/context/LibraryContext";

import AgentsCollection from "views/agent/collection/AgentsCollection";
import NewAgent from "views/agent/collection/new-agent/AgentNew";
import AgentInfoLayout from "layouts/agent/agent-info";
import Users from "views/users";
import UserInfoLayout from "layouts/user-info";
import PermissionRoute from "auth/PermissionRoute";
import ChatOnlyRoute from "auth/ChatOnlyRoute";
import SidebarHorizon from "components/Sidebar/MainSideBar";
import { PERMISSIONS } from "auth/permissions";
import IntegrationsView from "views/integrations";
import GlobalSettings from "layouts/global-settings";
import OrganizationSettings from "layouts/organization-settings";
import ApiKeys from "views/api-keys";
import Feedbacks from "views/feedback";
import TechnicalNotes from "views/technical-notes";
import Subscriptions from "views/subscriptions";
import SubscriptionPlans from "views/subscription-plans";
import UsageOverviewLayout from "layouts/usage-overview";
import MarketplaceView from "views/marketplace";

export default function Dashboard(props: Readonly<{ [x: string]: any }>) {
  const { t } = useTranslation();
  const { ...rest } = props;
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(t("dashboard.title"));
  useEffect(() => {
    window.addEventListener("resize", () =>
      window.innerWidth < 1200 ? setOpen(false) : setOpen(true)
    );
  }, []);
  // functions for changing the states from components
  const getActiveRoute = (routes: RoutesType[]): string => {
    let activeRoute = t("dashboard.title");
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].items);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          const routeName =
            routes[i].nameKey && typeof routes[i].nameKey === "string"
              ? t(routes[i].nameKey)
              : routes[i].name;
          setCurrentRoute(routeName);
        }
      }
    }
    return activeRoute;
  };

  useEffect(() => {
    getActiveRoute(routes);
    // eslint-disable-next-line
  }, [location.pathname, t]);
  const getActiveNavbar = (routes: RoutesType[]): boolean => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbar(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].secondary;
        }
      }
    }
    return activeNavbar;
  };

  document.documentElement.dir = "ltr";
  return (
    <div className="bg-background-100 dark:bg-background-900 flex h-full w-full">
      <SidebarHorizon
        open={open}
        hovered={hovered}
        setHovered={setHovered}
        mini={props.mini}
        onClose={() => setOpen(false)}
      />
      {/* Navbar & Main Content */}
      <div className="bg-background-600 h-full w-full font-dm dark:bg-navy-900">
        {/* Main Content */}
        <main
          className={`mx-2.5 flex-none transition-all dark:bg-navy-900 md:pr-2 ${
            open
              ? props.mini === false
                ? "xl:ml-[313px]"
                : props.mini === true && hovered === true
                ? "xl:ml-[313px]"
                : "ml-0 xl:ml-[142px]"
              : "ml-0 xl:ml-0"
          } `}
        >
          {/* Routes */}
          <div>
            <Portal>
              <Navbar
                onOpenSidenav={() => setOpen((prev) => !prev)}
                brandText={currentRoute}
                secondary={getActiveNavbar(routes)}
                theme={props.theme}
                open={open}
                setTheme={props.setTheme}
                hovered={hovered}
                mini={props.mini}
                setMini={props.setMini}
                {...rest}
              />
            </Portal>
            <div className="b item mx-auto flex min-h-screen flex-col p-2 !pt-[100px] md:p-2">
              <Routes>
                <Route
                  path="/corganization"
                  element={
                    <ChatOnlyRoute>
                      <CurrentOrgInfoLayout />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/cworkspace"
                  element={
                    <ChatOnlyRoute>
                      <CurrentWorkspaceInfoLayout />
                    </ChatOnlyRoute>
                  }
                />
                <Route path="/chat/*" element={<AgentChat />} />
                <Route
                  path="/workspaces"
                  element={
                    <ChatOnlyRoute>
                      <WorkspacesView />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/integrations"
                  element={
                    <ChatOnlyRoute>
                      <IntegrationsView />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/library"
                  element={
                    <ChatOnlyRoute>
                      <LibraryProvider>
                        <LibraryViewSwitch />
                      </LibraryProvider>
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/roles"
                  element={
                    <ChatOnlyRoute>
                      <Roles />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ChatOnlyRoute>
                      <Users />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/feedback"
                  element={
                    <ChatOnlyRoute>
                      <Feedbacks />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/feedback/:id"
                  element={
                    <ChatOnlyRoute>
                      <Feedbacks />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/technical-notes"
                  element={
                    <ChatOnlyRoute>
                      <TechnicalNotes />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/technical-notes/:id"
                  element={
                    <ChatOnlyRoute>
                      <TechnicalNotes />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/subscriptions"
                  element={
                    <ChatOnlyRoute>
                      <PermissionRoute permission={PERMISSIONS.SUPER_ADMIN}>
                        <Subscriptions />
                      </PermissionRoute>
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/subscriptions/:id"
                  element={
                    <ChatOnlyRoute>
                      <PermissionRoute permission={PERMISSIONS.SUPER_ADMIN}>
                        <Subscriptions />
                      </PermissionRoute>
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/subscription-plans"
                  element={
                    <ChatOnlyRoute>
                      <PermissionRoute permission={PERMISSIONS.SUPER_ADMIN}>
                        <SubscriptionPlans />
                      </PermissionRoute>
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/subscription-plans/:id"
                  element={
                    <ChatOnlyRoute>
                      <PermissionRoute permission={PERMISSIONS.SUPER_ADMIN}>
                        <SubscriptionPlans />
                      </PermissionRoute>
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/api-keys"
                  element={
                    <ChatOnlyRoute>
                      <ApiKeys />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/roles/:id/*"
                  element={
                    <ChatOnlyRoute>
                      <RoleInfoLayout />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/users/:id/*"
                  element={
                    <ChatOnlyRoute>
                      <UserInfoLayout />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/profile/*"
                  element={
                    <ChatOnlyRoute>
                      <UserProfile />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/agents/info/:id/*"
                  element={
                    <ChatOnlyRoute>
                      <AgentInfoLayout />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/agents/create"
                  element={
                    <ChatOnlyRoute>
                      <PermissionRoute permission={PERMISSIONS.CREATE_AGENT}>
                        <NewAgent />
                      </PermissionRoute>
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/agents"
                  element={
                    <ChatOnlyRoute>
                      <AgentsCollection />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/global-settings/*"
                  element={
                    <ChatOnlyRoute>
                      <PermissionRoute permission={PERMISSIONS.SUPER_ADMIN}>
                        <GlobalSettings />
                      </PermissionRoute>
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/organization-settings/*"
                  element={
                    <ChatOnlyRoute>
                      <OrganizationSettings />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/usage-overview/*"
                  element={
                    <ChatOnlyRoute>
                      <UsageOverviewLayout />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/marketplace"
                  element={
                    <ChatOnlyRoute>
                      <MarketplaceView />
                    </ChatOnlyRoute>
                  }
                />
                <Route
                  path="/"
                  element={<Navigate to="/dashboard/agents" replace />}
                />
              </Routes>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
